import { useTranslation } from 'react-i18next';
import { Modal } from '../../UI';
import type { UnscheduledIntervention } from '../../../types/weekOverview';
import { UnscheduledInterventionItem } from './UnscheduledInterventionItem';
import styles from './UnscheduledInterventionsModal.module.css';

const MODAL_WIDTH_PX = 560;
// The list grows with the number of unscheduled interventions, so the dialog
// is capped and its content region scrolls instead of outgrowing the viewport.
const MODAL_MAX_HEIGHT = '90vh';

interface UnscheduledInterventionsModalProps {
  interventions: readonly UnscheduledIntervention[];
  patientNameById: ReadonlyMap<string, string>;
  // Named in the title only when the dialog is opened from the year plan, where the week it belongs
  // to is not otherwise on screen. The week view already has the week in its own header.
  weekNumber?: number;
  onClose: () => void;
}

// Dialog listing the interventions the latest calculation could not place in
// the week's calendar, so the planner can resolve them (free up rooms/staff or
// move other bookings) instead of hunting for what is missing.
//
// Takes the interventions rather than a whole WeekData: the year plan reaches
// this from a per-week summary of the solve (see `buildWeekUnscheduledSummary`)
// and never maps the full week.
export const UnscheduledInterventionsModal = ({
  interventions,
  patientNameById,
  weekNumber,
  onClose
}: UnscheduledInterventionsModalProps) => {
  const { t } = useTranslation();

  const title =
    weekNumber === undefined
      ? t('week-overview-unscheduled-title')
      : t('week-overview-unscheduled-title-week', { week: weekNumber });

  return (
    <Modal
      title={title}
      onClose={onClose}
      width={MODAL_WIDTH_PX}
      style={{ maxHeight: MODAL_MAX_HEIGHT, display: 'flex', flexDirection: 'column' }}
      headerStyle={{ flexShrink: 0 }}
      contentStyle={{ padding: 'var(--spacing-md)', overflowY: 'auto', minHeight: 0 }}
    >
      <p className={styles.description}>{t('week-overview-unscheduled-description')}</p>
      <ul className={styles.list}>
        {interventions.map((intervention) => (
          <UnscheduledInterventionItem
            key={intervention.id}
            intervention={intervention}
            patientNameById={patientNameById}
          />
        ))}
      </ul>
    </Modal>
  );
};
